import { Editor } from "@tiptap/react";
import {
  DOMParser,
  DOMSerializer,
  Fragment,
  Schema,
  Slice,
} from "prosemirror-model";
import {
  EditorView,
  __parseFromClipboard as parseFromClipboard,
} from "prosemirror-view";
import { useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import { updateUser } from "../api/User";
import EmailSelectorFamily, { EmailMode } from "../atoms/EmailAtom";
import { UserAtom } from "../atoms/UserAtom";
import Warning from "../components/Modal/container/Warning";
import { useModal } from "../components/Modal/Modal";
import { localStorageSetItem } from "../utils/LocalStorage";
import {
  EDITOR_KEY,
  EmailSubjectKeysType,
  JUMP_BACK_KEY,
  JUMP_FORTH_KEY,
  setTimeoutAsync,
  SharedHistory,
  SPLIT_SUBJECT_KEY,
  SUBJECT_KEY,
} from "./useEmailEditorHistory";

const blockRegex =
  /^(address|blockquote|body|center|dir|div|dl|fieldset|form|h[1-6]|hr|isindex|menu|noframes|noscript|ol|p|pre|table|ul|dd|dt|frameset|li|tbody|td|tfoot|th|thead|tr|html)$/i;

export const parseFragmentToString = (fragment: DocumentFragment): string => {
  //Outputting the fragment content using a throwaway intermediary DOM element (div):
  const div = document.createElement("div");
  div.appendChild(fragment.cloneNode(true));
  return div.innerHTML;
};

export const parseFragmentStringToNode = (
  content: string,
  schema: Schema
): Slice => {
  let domNode = document.createElement("div");
  domNode.innerHTML = content;
  return DOMParser.fromSchema(schema).parseSlice(domNode);
};

export const parseSliceToFragment = (
  slice: Slice,
  schema: Schema
): DocumentFragment => {
  return DOMSerializer.fromSchema(schema).serializeFragment(slice.content);
};

export const parsePMFragmentToFragment = (
  fragment: Fragment,
  schema: Schema
): DocumentFragment => {
  return DOMSerializer.fromSchema(schema).serializeFragment(fragment);
};

export function sliceSingleNode(slice: Slice) {
  return slice.openStart == 0 &&
    slice.openEnd == 0 &&
    slice.content.childCount == 1
    ? slice.content.firstChild
    : null;
}

export function trimEmailHTML(content: string) {
  let domNode = document.createElement("div");
  domNode.innerHTML = content;

  let childNodeToRemove: ChildNode[] = [];

  for (let index = 0; index < domNode.childNodes.length; index++) {
    const currentChildNode = domNode.childNodes[index];

    if (
      !blockRegex.test(currentChildNode.nodeName) ||
      currentChildNode.textContent.trim()
    ) {
      break;
    }

    childNodeToRemove.push(currentChildNode);
  }

  for (let index = domNode.childNodes.length - 1; index > 0; index--) {
    const currentChildNode = domNode.childNodes[index];

    if (
      !blockRegex.test(currentChildNode.nodeName) ||
      currentChildNode.textContent.trim()
    ) {
      break;
    }

    childNodeToRemove.push(currentChildNode);
  }

  childNodeToRemove.forEach((child) => {
    domNode.removeChild(child);
  });

  return domNode.innerHTML;
}

export function addBoldInLinks(html: string) {
  const modifiedString = html
    .replace(/<a href/g, "<strong><a href ")
    .replace(/<\/a>/g, "</a></strong>");

  return modifiedString;
}

export function convertPagesMarginsToLineBreaksOnChrome(html: string) {
  let htmlWrapper = document.createElement("div");
  htmlWrapper.innerHTML = html;
  const styleElement = htmlWrapper.querySelector("style");
  if (!styleElement) return;
  const styleRules = styleElement.innerText;
  const paragraphRules = styleRules.matchAll(/(p\.)(.|\n)*?(\})/g);
  const paragraphRulesClasses = Array.from(paragraphRules, (x) => x[0]);

  let paragraphClassMargins: {
    [className: string]: {
      top: boolean;
      bottom: boolean;
    };
  } = {};

  for (let j = 0; j < paragraphRulesClasses.length; j++) {
    const currentParagraphRulesClass = paragraphRulesClasses[j];
    const currentParagraphMargins = currentParagraphRulesClass.match(
      /(margin:)(.|\n)*?(px\;)/
    )[0];

    let init = 0;
    let top = false;
    let bottom = false;

    for (let i = 0; i < 4; i++) {
      const v = currentParagraphMargins.slice(
        currentParagraphMargins.indexOf(" ", init) + 1,
        currentParagraphMargins.indexOf("px", init)
      );
      if (i === 0) {
        top = !!parseInt(v);
      }
      if (i === 2) {
        bottom = !!parseInt(v);
      }
      init = currentParagraphMargins.indexOf("px", init) + 2;
    }

    if (top || bottom)
      paragraphClassMargins[`p${j + 1}`] = {
        top,
        bottom,
      };
  }

  for (let className of Object.keys(paragraphClassMargins)) {
    const paragraphs = htmlWrapper.querySelectorAll(`.${className}`);
    const paragraphsArray = Array.from(paragraphs);
    for (let l of paragraphsArray) {
      if (l.textContent.trim()) {
        if (paragraphClassMargins[className].top)
          l.insertAdjacentHTML("beforebegin", "<p></p>");
        if (paragraphClassMargins[className].bottom)
          l.insertAdjacentHTML("afterend", "<p></p>");
      }
    }
  }

  return htmlWrapper.innerHTML;
}

export function convertPagesMarginsToLineBreaksOnWebkit(html: string) {
  let htmlWrapper = document.createElement("div");
  htmlWrapper.innerHTML = html;

  Array.from(htmlWrapper.querySelectorAll("p")).map((paragraph) => {
    const currentParagraphMargins = paragraph.outerHTML.match(
      /(margin:)(.|\n)*?(px\;)/
    )?.[0];

    if (!currentParagraphMargins) return;

    let init = 0;
    let top = false;
    let bottom = false;

    for (let i = 0; i < 4; i++) {
      const v = currentParagraphMargins.slice(
        currentParagraphMargins.indexOf(" ", init) + 1,
        currentParagraphMargins.indexOf("px", init)
      );
      if (i === 0) {
        top = !!parseInt(v);
      }
      if (i === 2) {
        bottom = !!parseInt(v);
      }
      init = currentParagraphMargins.indexOf("px", init) + 2;
    }

    if (paragraph.textContent.trim()) {
      if (top) paragraph.insertAdjacentHTML("beforebegin", "<p></p>");
      if (bottom) paragraph.insertAdjacentHTML("afterend", "<p></p>");
    }
  });

  return htmlWrapper.innerHTML;
}

export function useEmailEditorPasting(
  editor: Editor,
  sharedHistoryRef: React.MutableRefObject<SharedHistory>,
  mode: EmailMode,
  index: number
) {
  const [email, setEmail] = useRecoilState(
    EmailSelectorFamily({ mode, index })
  );
  const modalHandler = useModal();
  const [user, setUser] = useRecoilState(UserAtom);

  const lastEventRef = useRef<number>(0);
  const fastBackspaceRef = useRef<boolean>(false);

  // HERE!
  // @ts-ignore
  const splitMode = email.splitMode && user?.emailOptions.splitTesting;

  function handleKeyDown(event: KeyboardEvent) {
    /**
     * Triggered events from TextExpander are inhuman fast when
     * keying up, usually they take about 0.9 ms to keyup after a keydown.
     * So, I always listen for ctrl+v's and compare if the keyup - keydown time is
     * less than 15 ms (15 is arbitrary)
     */

    if (event.code === "Backspace" && event.type === "keydown") {
      lastEventRef.current = event.timeStamp;
    } else if (event.code === "Backspace" && event.type === "keyup") {
      if (event.timeStamp - lastEventRef.current < 10) {
        fastBackspaceRef.current = true;
      } else {
        fastBackspaceRef.current = false;
      }
    }
  }

  useEffect(() => {
    /**
     * Events for Text Expander checks
     */

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    window.addEventListener("keyup", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
      window.removeEventListener("keyup", handleKeyDown);
    };
  }, []);

  const pasteContent = (
    view: EditorView,
    e: ClipboardEvent,
    slice: Slice,
    hasFirstLineAsSubject?: boolean
  ) => {
    if (!slice) return false;
    let singleNode = sliceSingleNode(slice);

    let tr = singleNode
      ? view.state.tr.replaceSelectionWith(singleNode, (view as any).shiftKey)
      : view.state.tr.replaceSelection(slice);

    setTimeoutAsync(() => {
      if (hasFirstLineAsSubject && slice.size)
        sharedHistoryRef.current.save(EDITOR_KEY);
    }, 0).then(() => {
      view.dispatch(
        tr
          .scrollIntoView()
          .setMeta("paste", true)
          .setMeta("uiEvent", "paste")
          .setMeta("hasFirstLineAsSubject", hasFirstLineAsSubject)
      );
      if (hasFirstLineAsSubject)
        sharedHistoryRef.current.save(
          JUMP_BACK_KEY,
          (splitMode ? 4 : 3) - (!slice.size ? 1 : 0)
        );
    });

    return true;
  };

  function addExtraCarriage(
    view: EditorView,
    e: ClipboardEvent,
    slice: Slice,
    hasFirstLineAsSubject?: boolean
  ) {
    if (!editor) return null;

    const fragment = parseSliceToFragment(slice, view.state.schema);

    let content = parseFragmentToString(fragment);

    let domNode = document.createElement("div");
    domNode.innerHTML = content;

    for (let index = 0; index < domNode.childNodes.length - 1; index++) {
      const currentChildNode = domNode.childNodes[index];
      const nextChildNode = domNode.childNodes[index + 1];

      if (!blockRegex.test(currentChildNode.nodeName)) continue;

      if (
        currentChildNode.textContent.trim() &&
        nextChildNode.textContent.trim()
      ) {
        const paragraph = document.createElement("p");
        domNode.insertBefore(paragraph, nextChildNode);
      }
    }

    const contentAfterExtraCarriage = domNode.innerHTML;

    slice = parseFragmentStringToNode(
      contentAfterExtraCarriage,
      view.state.schema
    );

    pasteContent(view, e, slice, hasFirstLineAsSubject);

    if (mode === "new-email") {
      localStorageSetItem("DRAFT_EMAIL_BODY", contentAfterExtraCarriage);
    }
  }

  async function doNotWarnAddExtraCarriageUser({
    extraCarriage,
  }: {
    extraCarriage: boolean;
  }) {
    const newUser = {
      ...user,
      options: {
        ...user.options,
        extraCarriage,
        doNotWarnExtraCarriage: true,
      },
    };

    const isSuccess = await updateUser(newUser);

    if (isSuccess) setUser(newUser);
  }

  function checkEmailBodyAddExtraCarriage(
    view: EditorView,
    e: ClipboardEvent,
    slice: Slice,
    hasFirstLineAsSubject?: boolean
  ) {
    let content = (e.target as any)?.parentElement;

    let contentIsCopiedFromBm =
      content?.firstChild?.className?.includes("fromBM") ||
      content?.className?.includes("fromBM") ||
      false;

    if (user.options.doNotWarnExtraCarriage) {
      if (user.options.extraCarriage && !contentIsCopiedFromBm) {
        setTimeout(() => {
          addExtraCarriage(view, e, slice, hasFirstLineAsSubject);
        }, 0);
      } else {
        pasteContent(view, e, slice, hasFirstLineAsSubject);
      }
      return;
    }

    modalHandler.pushModal(
      <Warning
        icon={"./icons/modal/page.svg"}
        title={"Pasting Content (2/2)"}
        description="Do you want to add extra carriage returns to format as plain text?"
        descriptionCheck="Do not warn me again"
        buttonsName={["No", "Yes"]}
        onOkClicked={async (checked) => {
          if (checked) {
            doNotWarnAddExtraCarriageUser({ extraCarriage: true });
          }

          addExtraCarriage(view, e, slice, hasFirstLineAsSubject);

          modalHandler.popModal();
        }}
        onCancelClicked={async (checked) => {
          if (checked) {
            doNotWarnAddExtraCarriageUser({ extraCarriage: false });
          }

          pasteContent(view, e, slice, hasFirstLineAsSubject);

          modalHandler.popModal();
        }}
      />
    );
  }

  function addFirstLineAsSubject(
    view: EditorView,
    e: ClipboardEvent,
    slice: Slice
  ) {
    if (!editor) return null;

    const fragment = parseSliceToFragment(slice, view.state.schema);

    let html = parseFragmentToString(fragment);

    let domNode = document.createElement("div");
    domNode.innerHTML = html;

    const isBlock = blockRegex.test(domNode.childNodes[0].nodeName);

    let firstLine = isBlock
      ? domNode.childNodes[0].textContent.trim()
      : domNode.textContent;

    domNode.removeChild(domNode.childNodes[0]);

    let restOfBody = isBlock ? trimEmailHTML(domNode.innerHTML) : "";

    setEmail((prevNewEmail) => ({
      ...prevNewEmail,
      subject: firstLine,
      splitSubject: firstLine,
    }));

    if (mode === "new-email") {
      localStorageSetItem("DRAFT_EMAIL_SUBJECT", firstLine);
    }

    if (mode === "new-email") {
      localStorageSetItem("DRAFT_EMAIL_BODY", restOfBody);
    }

    slice = parseFragmentStringToNode(restOfBody, view.state.schema);

    sharedHistoryRef.current.save(
      JUMP_FORTH_KEY,
      (splitMode ? 4 : 3) - (!slice.size ? 1 : 0)
    );

    setTimeoutAsync(() => {
      sharedHistoryRef.current.instances[SUBJECT_KEY].save(firstLine);
      if (splitMode) {
        sharedHistoryRef.current.instances[SPLIT_SUBJECT_KEY].save(firstLine);
      }
    }, 0).then(() => checkEmailBodyAddExtraCarriage(view, e, slice, true));
  }

  async function doNotWarnFirstLineUser({
    firstLineAsSubject,
  }: {
    firstLineAsSubject: boolean;
  }) {
    if (!user) return;

    const newUser = {
      ...user,
      options: {
        ...user.options,
        firstLineAsSubject,
        doNotWarnFirstLine: true,
      },
    };

    const isSuccess = await updateUser(newUser);

    if (isSuccess) setUser(newUser);
  }

  function checkEmailBodyAsFirstLine(
    view: EditorView,
    e: ClipboardEvent & { isFromSubject?: boolean; textFromSubject?: string },
    slice: Slice
  ) {
    const isEmpty = !!!view.state.doc.textContent.trim();
    const hasLink = view.dom.innerHTML.includes("<a");
    const hasSplitSubject =
      user.emailOptions.splitTesting &&
      // HERE!
      // @ts-ignore
      email.splitMode &&
      // HERE!
      // @ts-ignore
      email.splitSubject.trim();
    const hasSubject = email.subject.trim() !== "";

    if (hasSubject || hasSplitSubject || !isEmpty || hasLink) {
      checkEmailBodyAddExtraCarriage(view, e, slice);
      return;
    }
    if (user.options.doNotWarnFirstLine) {
      if (user.options.firstLineAsSubject) {
        setTimeout(() => {
          addFirstLineAsSubject(view, e, slice);
        }, 0);
      } else {
        checkEmailBodyAddExtraCarriage(view, e, slice);
      }
      return;
    }

    modalHandler.pushModal(
      <Warning
        icon={"./icons/modal/page.svg"}
        title={
          user.options.doNotWarnExtraCarriage
            ? "Pasting Content"
            : "Pasting Content (1/2)"
        }
        description="Do you want to make the first line of this block of content the subject of the email?"
        descriptionCheck={"Do not warn me again"}
        buttonsName={["No", "Yes"]}
        onOkClicked={async (checked) => {
          if (checked) {
            doNotWarnFirstLineUser({
              firstLineAsSubject: true,
            });
          }

          modalHandler.popModal();

          setTimeout(() => {
            addFirstLineAsSubject(view, e, slice);
          }, 300);
        }}
        onCancelClicked={async (checked) => {
          if (checked) {
            doNotWarnFirstLineUser({
              firstLineAsSubject: false,
            });
          }

          modalHandler.popModal();
          if (e.isFromSubject)
            setEmail({ ...email, subject: e.textFromSubject });
          else checkEmailBodyAddExtraCarriage(view, e, slice);
        }}
      />
    );
  }

  function onPasteFromTextArea(
    event: React.ClipboardEvent<HTMLTextAreaElement> & {
      isFromSubject?: boolean;
      textFromSubject?: string;
    },
    key: EmailSubjectKeysType
  ) {
    const isEmpty = !!!editor.view.state.doc.textContent.trim();
    const hasLink = editor.view.dom.innerHTML.includes("<a");
    const hasSplitSubject =
      user.emailOptions.splitTesting &&
      // HERE!
      // @ts-ignore
      email.splitMode &&
      // HERE!
      // @ts-ignore
      email.splitSubject.trim();
    const hasSubject = email.subject.trim() !== "";

    // If the user does not have the option to add the first line as a subject or if subject/body already exists, just return;
    if (
      (user.options.doNotWarnFirstLine && !user.options.firstLineAsSubject) ||
      hasSubject ||
      hasSplitSubject ||
      !isEmpty ||
      hasLink
    )
      return;

    // In case there's first line as subject, do not run the
    // onChange event, only this paste one.
    event.preventDefault();
    event.stopPropagation();

    let text: string = event.clipboardData.getData("text/plain");
    let html: string = event.clipboardData.getData("text/html");

    const isPlainText = !html;

    const slice = parseFromClipboard(
      editor.view,
      text,
      html,
      isPlainText,
      editor.state.selection.$from
    );
    event.isFromSubject = true;
    event.textFromSubject = text;
    editor.options.editorProps.handlePaste(
      editor.view,
      event as unknown as ClipboardEvent,
      slice
    );
  }

  const checkEmailBodyAsFirstLineRef = useRef(checkEmailBodyAsFirstLine);

  useEffect(() => {
    checkEmailBodyAsFirstLineRef.current = checkEmailBodyAsFirstLine;
  }, [checkEmailBodyAsFirstLine]);

  return {
    checkEmailBodyAsFirstLineRef,
    fastBackspaceRef,
    onPasteFromTextArea,
  };
}
